import {
  Constraint,
  ConstraintSku,
  GameArgsType,
  GameType,
  PlayerInfoNumberType,
  PlayerInfoStringType,
  PlayerInputsType,
  PurchasesType,
  TopicPackSku,
} from "@/types";
import Constraints from "./constraints/Constraints";
import Packs from "./packs/Packs";
import { assets } from "@/config";
import { shuffle, uniqueNumbers } from "@/app/helpers";

export default class Engine {
  static freshRound(
    game: GameType,
    purchases: PurchasesType | null,
    ignoredTopicSkus: TopicPackSku[] = [],
    ignoredConstraintSkus: ConstraintSku[] = [],
    galleryMode = false
  ): GameArgsType {
    const mode = galleryMode ? "gallery" : "topics";
    const { created, id, ownerId, players, scores } = game;
    const constraint = Engine.generateConstraint(ignoredConstraintSkus);
    const topics = Engine.generateTopics(purchases, ignoredTopicSkus, constraint.topics);
    const playerIds = Object.keys(game.players);
    const roles = Engine.generateRoles(playerIds);
    const gallery = Engine.generateGallery();
    const newGame: GameArgsType = {
      created,
      deaths: {},
      id,
      ownerId,
      players,
      constraint: constraint.sku,
      mode,
      responses: {},
      roles,
      scores,
      state: "responses",
      gallery,
      topics,
      votes: {},
    };
    return newGame;
  }

  static generateConstraint(ignoredConstraintSkus: ConstraintSku[]): Constraint {
    const constraints: Constraint[] = [];
    Object.values(Constraints.constraintList).forEach((constraint) => {
      if (!ignoredConstraintSkus.includes(constraint.sku)) constraints.push(constraint);
    });
    return constraints[Math.floor(Math.random() * constraints.length)];
  }

  static generateDeaths(minDeaths: number, votes: PlayerInputsType): PlayerInfoNumberType {
    const tally: { [k: string]: number } = {};
    for (const k in votes) {
      (votes[k].value as string[]).forEach((id: string) => {
        tally[id] = tally[id] || 0;
        tally[id]++;
      });
    }
    const sorted = Object.keys(tally).sort((a, b) => {
      if (tally[a] > tally[b]) return -1;
      if (tally[a] < tally[b]) return 1;
      return 0;
    });
    const response: PlayerInfoNumberType = {};
    let hanging = true;
    for (let i = 0; i < sorted.length; i++) {
      const id = sorted[i];
      if (hanging && Object.keys(response).length < minDeaths) {
        response[id] = tally[sorted[i]];
      } else if (hanging) {
        if (tally[sorted[i]] === tally[sorted[i - 1]]) response[id] = tally[sorted[i]];
        else hanging = false;
      }
    }

    return response;
  }

  static generateResults(game: GameType): GameArgsType {
    const { roles, scores, votes } = game;
    const deathCount = Engine.imposterCount(Object.keys(votes).length);
    const deaths = Engine.generateDeaths(deathCount, votes);
    const newScores = Engine.generateScores(roles, scores, deaths);
    const resultGame: GameArgsType = {
      ...game,
      state: "results",
      deaths,
      scores: newScores,
    };
    return resultGame;
  }

  static generateScores(
    roles: PlayerInfoStringType,
    scores: PlayerInfoNumberType,
    deaths: PlayerInfoNumberType
  ): PlayerInfoNumberType {
    Object.keys(roles).forEach((id) => {
      const role = roles[id];
      const points = role === "agent" ? 1 : 2;
      scores[id] = scores[id] || 0;
      if (!deaths[id]) scores[id] += points;
    });
    return scores;
  }

  static generateTopics(
    purchases: PurchasesType | null,
    ignoredTopicSkus: TopicPackSku[] = [],
    count = 3
  ): string[] {
    const purchasedSkus = Object.values(purchases || {}).map((p) => p.sku as TopicPackSku);
    const packs = new Packs(purchasedSkus, ignoredTopicSkus);
    return packs.pluck(count);
  }

  static generateGallery(): string[] {
    const { min, max, pad, prefix, ext } = assets.people;
    const nums = uniqueNumbers(2, min, max);
    return nums.map((num) => `people/${prefix}${num.toString().padStart(pad, "0")}.${ext}`);
  }

  static generateRoles(playerIds: string[]): PlayerInfoStringType {
    const playerCount = playerIds.length;
    const imposterCount = Engine.imposterCount(playerCount);
    const rolesArray = [];
    for (let i = 0; i < playerCount; i++) {
      if (i < imposterCount) rolesArray.push("imposter");
      else rolesArray.push("agent");
    }
    const roles: PlayerInfoStringType = {};
    shuffle(rolesArray).forEach((role, i) => (roles[playerIds[i]] = role));
    return roles;
  }

  static imposterCount(playerCount: number) {
    return Math.max(1, Math.round(playerCount * 0.33));
  }
}
